import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { listSpendingTransactions } from "../../lib/api-modules/spending-transactions.js";
import { listIncomeEvents } from "../../lib/api-modules/income-events.js";
import { listBills } from "../../lib/api-modules/bills.js";
import { evaluateSavingRateTrend } from "../../lib/util/saving-rate-trend.js";
import { Card, Pill } from "../design-system/index.js";

/**
 * Phase 13 — Saving rate trend dashboard tile.
 *
 * 12-month rolling savings-rate series + month-over-month + year-over-
 * year deltas + status pill. Status pill resolves vs. the household's
 * required FIRE savings rate when passed in (from FireTile's existing
 * computation); otherwise renders an "unknown" pill.
 *
 * Composes existing income / bills / spending list endpoints; runs the
 * pure helper client-side. No new API endpoint.
 */

interface Props {
  readonly today: string; // YYYY-MM-DD
  readonly requiredSavingsRatePct?: number | undefined;
}

export function SavingRateTrendTile({ today, requiredSavingsRatePct }: Props) {
  const minus = (months: number): string => {
    const d = new Date(`${today}T00:00:00Z`);
    d.setUTCMonth(d.getUTCMonth() - months);
    return d.toISOString().slice(0, 10);
  };
  // Need 12 months of trailing-12 series → 24 months of source data.
  const fromIso = minus(24);

  const spendQ = useQuery({
    queryKey: ["saving-rate-trend", "spending", fromIso, today],
    queryFn: () => listSpendingTransactions({ from: fromIso, to: today }),
  });
  const incomeQ = useQuery({
    queryKey: ["saving-rate-trend", "income", fromIso, today],
    queryFn: () => listIncomeEvents({ from: fromIso, to: today }),
  });
  const billsQ = useQuery({
    queryKey: ["saving-rate-trend", "bills"],
    queryFn: () => listBills(),
  });

  const result = useMemo(() => {
    if (!spendQ.data || !incomeQ.data || !billsQ.data) return null;
    return evaluateSavingRateTrend({
      income_events: incomeQ.data.income_events,
      bills: billsQ.data.bills,
      spending_transactions: spendQ.data.spending_transactions,
      today,
      ...(requiredSavingsRatePct !== undefined
        ? { required_savings_rate_pct: requiredSavingsRatePct }
        : {}),
    });
  }, [spendQ.data, incomeQ.data, billsQ.data, today, requiredSavingsRatePct]);

  if (spendQ.isLoading || incomeQ.isLoading || billsQ.isLoading) {
    return (
      <Card>
        <h3 className="mb-3 font-display text-lg font-medium text-[var(--color-text)]">
          Saving rate trend
        </h3>
        <p className="text-sm text-[var(--color-muted)]">Loading…</p>
      </Card>
    );
  }
  if (!result) {
    return (
      <Card>
        <h3 className="mb-3 font-display text-lg font-medium text-[var(--color-text)]">
          Saving rate trend
        </h3>
        <p className="text-sm text-[var(--color-muted)]">
          No data yet — log some income + spending to see your trend.
        </p>
      </Card>
    );
  }

  const tone = statusTone(result.status);
  const chartData = result.series.map((p) => ({
    period: p.period.slice(2), // YY-MM for axis brevity
    rate: round1(p.trailing_12_rate_pct),
  }));

  return (
    <Card>
      <div className="mb-3 flex items-start justify-between gap-3">
        <h3 className="font-display text-lg font-medium text-[var(--color-text)]">
          Saving rate trend
        </h3>
        <Pill tone={tone}>
          {STATUS_LABEL[result.status]}
          {result.required_savings_rate_pct !== undefined &&
          result.required_savings_rate_pct !== null
            ? ` (req ${round1(result.required_savings_rate_pct)}%)`
            : ""}
        </Pill>
      </div>
      <div className="mb-3 grid grid-cols-3 gap-3 text-sm">
        <div>
          <p className="text-xs uppercase text-[var(--color-muted)]">Current</p>
          <p className="font-mono text-lg text-[var(--color-text)]">
            {round1(result.current_trailing_12_pct)}%
          </p>
        </div>
        <div>
          <p className="text-xs uppercase text-[var(--color-muted)]">MoM</p>
          <p
            className={`font-mono text-lg ${result.mom_delta_pp >= 0 ? "text-[var(--color-success)]" : "text-[var(--color-danger)]"}`}
          >
            {result.mom_delta_pp >= 0 ? "+" : ""}
            {round1(result.mom_delta_pp)}pp
          </p>
        </div>
        <div>
          <p className="text-xs uppercase text-[var(--color-muted)]">YoY</p>
          <p
            className={`font-mono text-lg ${result.yoy_delta_pp >= 0 ? "text-[var(--color-success)]" : "text-[var(--color-danger)]"}`}
          >
            {result.yoy_delta_pp >= 0 ? "+" : ""}
            {round1(result.yoy_delta_pp)}pp
          </p>
        </div>
      </div>
      <div className="h-32 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="srGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-accent)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="var(--color-accent)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="period"
              tick={{ fontSize: 10, fill: "var(--color-muted)" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "var(--color-muted)" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `${v}%`}
              width={32}
            />
            <Tooltip
              contentStyle={{
                background: "var(--color-surface)",
                border: "1px solid var(--color-border)",
                fontSize: 12,
              }}
              formatter={(v) => [`${v as number}%`, "Saving rate"]}
            />
            <Area
              type="monotone"
              dataKey="rate"
              stroke="var(--color-accent)"
              strokeWidth={2}
              fill="url(#srGrad)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

const STATUS_LABEL: Record<
  ReturnType<typeof evaluateSavingRateTrend>["status"],
  string
> = {
  on_track: "On track",
  slipping: "Slipping",
  critical: "Critical",
  unknown: "No target",
};

function statusTone(
  status: ReturnType<typeof evaluateSavingRateTrend>["status"],
): "success" | "warning" | "danger" | "neutral" {
  if (status === "on_track") return "success";
  if (status === "slipping") return "warning";
  if (status === "critical") return "danger";
  return "neutral";
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
